/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import configData from "config/config.json";
import useStyles from "styles/store/storeEditor";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { delay } from "utils/miscUtils";
import { getStoreInfo, create, update } from "services/storeService";
import { storeValidations } from "utils/validation";
import sessionHelperInstance from "services/sessionHelper";

const _storeDetails = {
  type: "COINS",
  purchaseMethod: "REAL_CASH",
  pbfCoin: 0,
  buyPrice: {},
  bonus: 0,
  buyCoins: 0,
  bonusTxt: "",
};

const StoreEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [storeDetails, setStoreDetails] = useState(_storeDetails);
  const [buyPrices, setBuyPrices] = useState([{ currency: "", price: 0 }]);

  const cancel = () => navigate(configData.routes.store.store);

  const submit = async () => {
    setLoading(true);
    try {
      // convert to number & add buy prices to store details
      const { pbfCoin, bonus, buyCoins, ...rest } = storeDetails;
      let _latestStoreDetails = {
        ...rest,
        pbfCoin: Number(pbfCoin),
        bonus: Number(bonus),
        buyCoins: Number(buyCoins),
      };

      _latestStoreDetails.buyPrice = {};

      for (const { currency, price } of buyPrices)
        _latestStoreDetails.buyPrice[`${currency}`] = price;

      // Validate data and create/update store
      await storeValidations.validate(_latestStoreDetails);
      let response;

      if (id === "create") {
        response = await create(_latestStoreDetails);
      } else {
        response = await update(_latestStoreDetails);
      }
      setLoading(false);
      showSnackbar(response?.message ?? "Store updated successfully");
      delay(100);
      navigate(configData.routes.store.store);
    } catch (error) {
      setLoading(false);
      if (error.inner)
        error.inner.forEach((error) => showSnackbar(error?.message, "error"));
      showSnackbar(error?.message, "error");
    }
  };

  const fetchStoreInfo = async () => {
    try {
      const resp = await getStoreInfo(id);

      const _buyPrices = [];
      if (resp?.data) {
        const { buyPrice: updatedBP } = resp?.data;
        for (const _currency of Object.keys(updatedBP)) {
          _buyPrices.push({ currency: _currency, price: updatedBP[_currency] });
        }

        setBuyPrices(_buyPrices);
      }
      setStoreDetails(resp.data);
      showSnackbar(resp?.message);
    } catch (error) {
      showSnackbar(error.message ?? "Failed to fetch store info", "error");
    }
  };

  const onChange = (e: any) => {
    const { name, value } = e.target;
    setStoreDetails((st) => ({ ...st, [name]: value }));
  };

  const addBuyPrice = (e: any) =>
    setBuyPrices((st) => [...st, { currency: "", price: 0 }]);

  const handleBuyPriceChange = (e, index) => {
    const { name, value } = e?.target;
    setBuyPrices((st) => [
      ...st.slice(0, index),
      {
        ...st[index],
        [name]: name === "price" ? parseFloat(value) || 0 : value,
      },
      ...st.slice(index + 1),
    ]);
  };

  const removeBuyPrice = (index: any) =>
    setBuyPrices((prevState) => {
      return [...prevState.slice(0, index), ...prevState.slice(index + 1)];
    });

  useEffect(() => {
    if (id !== "create") fetchStoreInfo();
  }, []);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} Store`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box>
        <Box className={styles.container}>
          <Box style={{ display: "flex", flexFlow: "row wrap", gap: 10 }}>
            <Box className={styles.field}>
              <SimpleTextInput
                label="PBF Coin"
                name="pbfCoin"
                type="number"
                value={storeDetails.pbfCoin}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Bonus"
                name="bonus"
                type="number"
                value={storeDetails.bonus}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Type"
                name="type"
                value={storeDetails.type}
                readOnly={true}
                readonlyvalue={storeDetails.type}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Purchase Method"
                name="purchaseMethod"
                value={storeDetails.purchaseMethod}
                readOnly={true}
                readonlyvalue={storeDetails.purchaseMethod}
              />
            </Box>
            {/* <Box className={styles.field}>
              <SimpleTextInput
                label="Buy Coins"
                name="buyCoins"
                type="number"
                value={storeDetails.buyCoins}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.field}>
              <SimpleTextInput
                label="Bonus Text"
                name="bonusTxt"
                value={storeDetails.bonusTxt}
                onChange={onChange}
              />
            </Box> */}
          </Box>
          <Box style={{ marginTop: 10 }}>
            <Typography fontSize={16} fontWeight={800}>
              Buy Prices
            </Typography>
            <Box style={{ marginBottom: 20 }}>
              <Button type="submit" variant="contained" onClick={addBuyPrice}>
                Add a Buy Price
              </Button>

              <Box
                style={{
                  display: "flex",
                  flexFlow: "column wrap",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                {buyPrices?.map((buyPrice, _) => {
                  return (
                    <Box
                      key={_}
                      style={{
                        display: "flex",
                        flexFlow: "row wrap",
                        gap: 10,
                      }}
                    >
                      <Box className={styles.field}>
                        <SimpleSelectInput
                          label="Currency"
                          name="currency"
                          value={buyPrice.currency}
                          options={sessionHelperInstance.getCurrencyOptions()}
                          customStyle={{ height: 35 }}
                          onChange={(e) => handleBuyPriceChange(e, _)}
                        />
                      </Box>
                      <Box className={styles.field}>
                        <SimpleTextInput
                          label="Price"
                          name="price"
                          type="number"
                          value={buyPrice.price}
                          customStyle={{ height: 35 }}
                          onChange={(e) => handleBuyPriceChange(e, _)}
                        />
                      </Box>
                      {_ !== 0 && (
                        <Button
                          style={{ height: 35, marginTop: 25 }}
                          size="small"
                          type="submit"
                          variant="contained"
                          onClick={() => removeBuyPrice(_)}
                        >
                          <DeleteForeverIcon sx={{ color: 'white' }} />
                          <span style={{ marginLeft: 5 }}>{"delete"}</span>
                        </Button>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ContentShell>
  );
};

export default StoreEditor;
